import { FocusMonitor } from '@angular/cdk/a11y';
import { Overlay, OverlayPositionBuilder, OverlayRef, ScrollStrategy } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  AfterViewInit,
  DestroyRef,
  Directive,
  ElementRef,
  inject,
  Input,
  NgZone,
  numberAttribute,
  OnDestroy,
  OnInit,
  TemplateRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { getOverlayPositions, OverlayPosition } from '@wheere-front/shared-util';
import { filter, fromEvent, merge, of, switchMap, tap } from 'rxjs';
import { TooltipComponent } from './tooltip.component';
import { TOOLTIP_CONFIG } from './tooltip.provider';

export type TooltipPosition = OverlayPosition;
export type TooltipMode = 'hover' | 'click';

@Directive({
  selector: '[libTooltip]',
  exportAs: 'libTooltip',
  standalone: true,
})
export class TooltipDirective implements OnInit, OnDestroy, AfterViewInit {
  @Input('libTooltip') text = '';
  @Input('libTooltipWithCaret') withCaret = false;
  @Input({ alias: 'libTooltipTemplate' })
  template: TemplateRef<unknown> | null = null;
  @Input({ alias: 'libTooltipTemplateCtx' })
  templateCtx: unknown;
  private config = inject(TOOLTIP_CONFIG);
  @Input('libTooltipPosition') position: TooltipPosition = this.config.position;
  @Input('libTooltipMode') mode: TooltipMode = this.config.mode;
  @Input({ alias: 'libTooltipPositionOffset', transform: numberAttribute })
  positionOffset = this.config.positionOffset;

  private overlay = inject(Overlay);
  private overlayPositionBuilder = inject(OverlayPositionBuilder);
  private elementRef = inject(ElementRef);
  private destroyRef = inject(DestroyRef);
  private ngZone = inject(NgZone);
  private focusMonitor = inject(FocusMonitor);
  private overlayRef!: OverlayRef;

  ngOnInit() {
    const positions = getOverlayPositions(this.position, this.positionOffset);

    const positionStrategy = this.overlayPositionBuilder
      .flexibleConnectedTo(this.elementRef)
      .withPositions(positions)
      .withFlexibleDimensions(false)
      .withViewportMargin(6);

    const scrollStrategy: ScrollStrategy = this.overlay.scrollStrategies.close({
      threshold: 20,
    });

    this.overlayRef = this.overlay.create({ positionStrategy, scrollStrategy });

    this.overlayRef
      .keydownEvents()
      .pipe(
        filter(e => e.key === 'Escape'),
        tap(() => this.hide()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    const hoverModeHostListener$ = of(this.mode).pipe(
      filter(() => this.mode === 'hover'),
      switchMap(() =>
        merge(
          fromEvent(this.elementRef.nativeElement, 'mouseenter').pipe(tap(() => this.show())),
          fromEvent(this.elementRef.nativeElement, 'mouseleave').pipe(tap(() => this.hide()))
        )
      )
    );

    const clickModeHostListener$ = of(this.mode).pipe(
      switchMap(() =>
        fromEvent(this.elementRef.nativeElement, 'click').pipe(
          filter(() => this.mode === 'click'),
          tap(() => this.toggle())
        )
      )
    );

    merge(hoverModeHostListener$, clickModeHostListener$).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  ngAfterViewInit() {
    // NOTE: the focus monitor runs outside the Angular zone.
    this.focusMonitor
      .monitor(this.elementRef)
      .pipe(
        tap(origin => {
          if (!origin) {
            this.ngZone.run(() => this.hide());
          } else if (origin === 'keyboard') {
            this.ngZone.run(() => this.show());
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.overlayRef?.dispose();
  }

  show() {
    // An empty binding is how callers express "conditionally no tooltip"
    // (`[libTooltip]="cond ? text : ''"`). Attaching with nothing to render makes
    // TooltipComponent throw on hover, so treat it as a no-op.
    if (!this.template && !this.text) return;

    if (!this.overlayRef?.hasAttached()) {
      const tooltipRef = this.overlayRef.attach(new ComponentPortal(TooltipComponent));

      if (this.template) {
        tooltipRef.instance.template = this.template;
        if (this.templateCtx) {
          tooltipRef.instance.templateCtx = this.templateCtx;
        }
      } else {
        tooltipRef.instance.text = this.text;
      }

      tooltipRef.instance.position = this.position;
      tooltipRef.instance.withCaret = this.withCaret;
    }
  }

  hide() {
    if (this.overlayRef?.hasAttached()) {
      this.overlayRef.detach();
    }
  }

  toggle() {
    if (this.overlayRef?.hasAttached()) {
      this.hide();
    } else {
      this.show();
    }
  }
}
